// Operational-tier primitives (design brief §13) — thin wrappers over ops.css.
// One grammar for status, phases, signals, facts, rails, drawers, keycaps.
// Every command-center surface composes these; none of them fetch — with ONE
// deliberate exception: SignalRowView's resolution controls (ack / snooze /
// assign / clear) call the /api/cc/signals/* mutations when the host page
// opts in by passing `onChanged` (Phase 3 signal resolution store).

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '@/components/Toast';
import {
  PHASE_LABEL,
  phaseTone,
  formatAge,
  formatWhen,
  SEVERITY_LABEL,
  isSignalResolved,
  ackSignal,
  snoozeSignal,
  assignSignal,
  clearSignalResolution,
  type Phase,
  type Signal,
  type SignalCounts,
  type SignalSeverity,
  type CaseFact,
  type RailStep,
} from '@/lib/commandCenterClient';

// ---------- Signal severity vocabulary ----------

export const SEVERITIES: SignalSeverity[] = ['block', 'attention', 'notice'];

/** Signal-severity chip tones (brief color law: danger only for blocks). */
export function severityTone(s: SignalSeverity): 'blocked' | 'active' | 'closed' {
  if (s === 'block') return 'blocked';
  if (s === 'attention') return 'active';
  return 'closed';
}

/** Compact per-severity count chips; none → muted em dash. Optional label text. */
export function SignalChips({ counts, labeled }: { counts: SignalCounts; labeled?: boolean }) {
  const present = SEVERITIES.filter((s) => counts[s] > 0);
  if (present.length === 0) return <span className="oc-meta">—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: '10px', flexWrap: 'wrap' }}>
      {present.map((s) => (
        <span key={s} className="oc-chip" data-tone={severityTone(s)} title={SEVERITY_LABEL[s]}>
          {counts[s]}
          {labeled ? ` ${SEVERITY_LABEL[s].toLowerCase()}` : ''}
        </span>
      ))}
    </span>
  );
}

// ---------- Submission / signer status vocabulary ----------

// The wire status codes SubmissionRow.status carries that mean "no longer in
// flight". Keyed off the STATUS CODE, never the display label.
const SUBMISSION_CLOSED_STATUSES: ReadonlySet<string> = new Set([
  'recalled',
  'superseded',
  'voided',
  'Cancelled',
]);

/** Overall submission status → chip tone (color law: danger only for
 *  declined, success for executed, muted for closed). */
export function submissionTone(status: string): 'done' | 'blocked' | 'closed' | 'active' {
  if (status === 'Fully Executed') return 'done';
  if (status === 'Declined') return 'blocked';
  if (SUBMISSION_CLOSED_STATUSES.has(status)) return 'closed';
  return 'active';
}

/** Per-signer status → chip tone. */
export function signerTone(status: string): 'done' | 'blocked' | 'closed' | 'active' {
  const s = status.trim().toLowerCase();
  if (s === 'completed') return 'done';
  if (s === 'declined') return 'blocked';
  if (s === 'opened' || s === 'sent') return 'active';
  return 'closed';
}

/** A submission is open/in-flight when its wire status isn't terminal. */
export function isSubmissionOpen(status: string): boolean {
  return (
    status !== 'Fully Executed' &&
    status !== 'Declined' &&
    !SUBMISSION_CLOSED_STATUSES.has(status)
  );
}

// ---------- StatusChip ----------

export function StatusChip({
  tone,
  children,
  sub,
}: {
  tone: 'blocked' | 'done' | 'closed' | 'active' | 'active-soft';
  children: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <span className="oc-chip" data-tone={tone}>
      {children}
      {sub != null && <span className="oc-chip-sub">{sub}</span>}
    </span>
  );
}

export function PhaseChip({ phase, sub }: { phase: Phase; sub?: ReactNode }) {
  return (
    <StatusChip tone={phaseTone(phase)} {...(sub != null ? { sub } : {})}>
      {PHASE_LABEL[phase]}
    </StatusChip>
  );
}

// ---------- PhaseBar ----------

export interface PhaseSegment {
  phase: Phase;
  count: number;
  href?: string;
}

export function PhaseBar({ segments }: { segments: PhaseSegment[] }) {
  const visible = segments.filter((s) => s.count > 0);
  const total = visible.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) return <span className="oc-phasebar" aria-hidden="true" />;
  return (
    <span className="oc-phasebar" role="img" aria-label={segmentsLabel(visible)}>
      {visible.map((s) =>
        s.href ? (
          <Link
            key={s.phase}
            to={s.href}
            data-seg={s.phase}
            style={{ flex: s.count }}
            title={`${s.count} ${PHASE_LABEL[s.phase].toLowerCase()}`}
          />
        ) : (
          <span
            key={s.phase}
            data-seg={s.phase}
            style={{ flex: s.count }}
            title={`${s.count} ${PHASE_LABEL[s.phase].toLowerCase()}`}
          />
        ),
      )}
    </span>
  );
}

function segmentsLabel(segments: PhaseSegment[]): string {
  return segments.map((s) => `${s.count} ${PHASE_LABEL[s.phase].toLowerCase()}`).join(' · ');
}

// ---------- SignalRow ----------

/** "snoozed until Aug 3" — date-only rendering of the snooze ceiling. */
function snoozeUntilLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * One signal row. Pass `onChanged` (a refetch) to enable the resolution
 * affordances — Ack / Snooze-to-date / Assign / Clear. A signal with an ACTIVE
 * resolution (acked, or snoozed and not yet unsealed) renders grayed via
 * `data-resolved` but stays visible; at the snooze date it unseals back to
 * normal visibility (the server stops muting it).
 */
export function SignalRowView({
  signal,
  onChanged,
}: {
  signal: Signal;
  onChanged?: () => void;
}) {
  const res = signal.resolution;
  const resolved = isSignalResolved(signal);
  return (
    <div
      className="oc-signal"
      data-severity={signal.severity}
      data-resolved={resolved ? (res?.snoozed ? 'snoozed' : 'acked') : undefined}
    >
      <span className="oc-signal-marker" aria-hidden="true" />
      <div className="oc-signal-body">
        <div className="oc-signal-fact">{signal.message}</div>
        <div className="oc-signal-meta">
          <span>{signal.categoryLabel}</span>
          <span>{signal.source}</span>
          {signal.ageDays != null && <span>{formatAge(signal.ageDays)}</span>}
          {res?.snoozed && res.snoozeUntil && (
            <span>snoozed until {snoozeUntilLabel(res.snoozeUntil)}</span>
          )}
          {!res?.snoozed && res?.acknowledgedAt && (
            <span>acknowledged {formatWhen(res.acknowledgedAt)}</span>
          )}
          {res?.assignedTo && <span>assigned to {res.assignedTo}</span>}
        </div>
        {res?.note && <div className="oc-signal-note">{res.note}</div>}
        {onChanged && <SignalResolutionControls signal={signal} onChanged={onChanged} />}
      </div>
      <span className="oc-signal-door">
        <Link to={signal.href}>Open</Link>
      </span>
    </div>
  );
}

/** Tomorrow's local date (yyyy-mm-dd) — the earliest allowed snooze ceiling. */
function tomorrowLocalDate(): string {
  const d = new Date(Date.now() + 86_400_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function SignalResolutionControls({
  signal,
  onChanged,
}: {
  signal: Signal;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<null | 'snooze' | 'assign'>(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState(signal.resolution?.assignedTo ?? '');

  const res = signal.resolution;
  const acked = !!res?.acknowledgedAt;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      setPanel(null);
      setNote('');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'The signal action failed.');
    } finally {
      setBusy(false);
    }
  };

  const submitSnooze = () => {
    if (!snoozeDate) return;
    // Local midnight of the chosen date — the signal unseals when that date
    // arrives for the staff picking it.
    const until = new Date(`${snoozeDate}T00:00:00`);
    if (Number.isNaN(until.getTime())) return;
    void run(() => snoozeSignal(signal.resolutionId, until.toISOString(), note.trim() || undefined));
  };

  return (
    <div className="oc-signal-actions">
      {!acked && (
        <button
          type="button"
          className="btn"
          data-variant="ghost"
          data-size="sm"
          disabled={busy}
          title="Mark handled — stays visible grayed while still detected"
          onClick={() => void run(() => ackSignal(signal.resolutionId))}
        >
          Ack
        </button>
      )}
      <button
        type="button"
        className="btn"
        data-variant="ghost"
        data-size="sm"
        disabled={busy}
        onClick={() => setPanel(panel === 'snooze' ? null : 'snooze')}
      >
        {res?.snoozeUntil ? 'Re-snooze…' : 'Snooze…'}
      </button>
      <button
        type="button"
        className="btn"
        data-variant="ghost"
        data-size="sm"
        disabled={busy}
        onClick={() => setPanel(panel === 'assign' ? null : 'assign')}
      >
        {res?.assignedTo ? 'Reassign…' : 'Assign…'}
      </button>
      {res && (
        <button
          type="button"
          className="btn"
          data-variant="ghost"
          data-size="sm"
          disabled={busy}
          title="Remove the resolution — the signal returns to normal visibility"
          onClick={() => void run(() => clearSignalResolution(signal.resolutionId))}
        >
          Clear
        </button>
      )}

      {panel === 'snooze' && (
        <span className="oc-signal-form">
          <input
            type="date"
            className="input"
            aria-label="Snooze until date"
            min={tomorrowLocalDate()}
            value={snoozeDate}
            disabled={busy}
            onChange={(e) => setSnoozeDate(e.target.value)}
          />
          <input
            type="text"
            className="input"
            aria-label="Optional note"
            placeholder="Note (optional)"
            style={{ width: 160 }}
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
          />
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy || !snoozeDate}
            onClick={submitSnooze}
          >
            Snooze
          </button>
        </span>
      )}

      {panel === 'assign' && (
        <span className="oc-signal-form">
          <input
            type="text"
            className="input"
            aria-label="Assign to operator"
            placeholder="Operator"
            style={{ width: 160 }}
            value={assignee}
            disabled={busy}
            onChange={(e) => setAssignee(e.target.value)}
          />
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy || !assignee.trim()}
            onClick={() => void run(() => assignSignal(signal.resolutionId, assignee.trim()))}
          >
            Assign
          </button>
          {res?.assignedTo && (
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              disabled={busy}
              onClick={() => void run(() => assignSignal(signal.resolutionId, null))}
            >
              Unassign
            </button>
          )}
        </span>
      )}
    </div>
  );
}

// ---------- FactList ----------

const SOURCE_LABEL: Record<CaseFact['source'], string> = {
  appfolio: 'AppFolio',
  staff: 'Staff',
  signer: 'Signer',
  derived: 'Derived',
};

export function SourceTag({ source }: { source: CaseFact['source'] }) {
  return <span className="oc-source">{SOURCE_LABEL[source]}</span>;
}

export function FactListView({ facts }: { facts: CaseFact[] }) {
  return (
    <div className="oc-facts">
      {facts.map((f, i) => (
        <div key={i} style={{ display: 'contents' }}>
          <span className="oc-fact-key">{f.label}</span>
          <span className="oc-fact-val">
            {f.value}
            <SourceTag source={f.source} />
            {f.note && (
              <span style={{ display: 'block', fontSize: '12px', color: 'var(--warning-fg)' }}>
                {f.note}
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------- LifecycleRail ----------

export function LifecycleRailView({ steps }: { steps: RailStep[] }) {
  return (
    <div className="oc-rail">
      {steps.map((s, i) => (
        <span key={s.state} style={{ display: 'inline-flex', alignItems: 'center' }}>
          {i > 0 && <span className="oc-rail-sep" aria-hidden="true" />}
          <span
            className="oc-rail-step"
            data-state={s.status === 'future' ? undefined : s.status}
          >
            {s.label}
          </span>
        </span>
      ))}
    </div>
  );
}

// ---------- KeyCap ----------

export function KeyCap({ children }: { children: ReactNode }) {
  return <kbd className="oc-kbd">{children}</kbd>;
}

// ---------- InspectorDrawer ----------

export function InspectorDrawer({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="oc-drawer-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="oc-drawer" role="dialog" aria-modal="true">
        <div className="oc-drawer-header">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-md)' }}>
              {title}
            </div>
            {subtitle != null && <div className="oc-meta">{subtitle}</div>}
          </div>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>
        <div className="oc-drawer-body">{children}</div>
        {footer != null && <div className="oc-drawer-footer">{footer}</div>}
      </aside>
    </>
  );
}

// ---------- Empty / loading states ----------

export function OcEmpty({ children }: { children: ReactNode }) {
  return <div className="oc-empty">{children}</div>;
}

export function OcLoading({ label = 'Loading…' }: { label?: string }) {
  return <div className="oc-empty">{label}</div>;
}

export function OcError({ children }: { children: ReactNode }) {
  return (
    <div className="alert" data-tone="danger" role="alert">
      <div className="alert-body">{children}</div>
    </div>
  );
}

// ---------- Two-track status ----------

export function TwoTrack({
  phase,
  nativeStateLabel,
  signature,
}: {
  phase: Phase;
  nativeStateLabel: string;
  signature: { signed: number; total: number; nextName: string | null } | null;
}) {
  return (
    <span className="oc-twotrack">
      <PhaseChip phase={phase} sub={nativeStateLabel} />
      {signature && signature.total > 0 && (
        <>
          <span className="oc-track-sep">·</span>
          <span style={{ fontVariantNumeric: 'var(--numerals-tabular)' }}>
            {signature.signed}/{signature.total} signed
            {signature.nextName ? ` · ${signature.nextName} next` : ''}
          </span>
        </>
      )}
    </span>
  );
}
